import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '@/shared/lib/utils';

export const MONTH_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
export const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export interface AnnualBarPoint {
  month: number; // 1-12
  value: number;
  isForecast?: boolean;
}

function compactCurrency(value: number): string {
  if (Math.abs(value) >= 1000) return `R$ ${(value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`;
  return `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

function ChartTooltip({ active, payload }: { active?: boolean; payload?: { payload: AnnualBarPoint }[] }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 text-sm shadow-md">
      <p className="font-medium text-foreground">
        {MONTH_NAMES[point.month - 1]}
        {point.isForecast && <span className="ml-2 text-xs font-normal text-muted-foreground">(previsto)</span>}
      </p>
      <p className="text-muted-foreground">{formatCurrency(point.value)}</p>
      <p className="mt-1 text-xs text-muted-foreground">Clique para ver o mês</p>
    </div>
  );
}

/**
 * Barras mês a mês de um ano (uma série). Meses previstos ficam mais claros.
 */
export function AnnualBarChart({
  data,
  color,
  onSelectMonth,
}: {
  data: AnnualBarPoint[];
  color: string;
  onSelectMonth?: (month: number) => void;
}) {
  const chartData = data.map((d) => ({ ...d, label: MONTH_SHORT[d.month - 1] }));
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} className="text-xs" tick={{ fill: 'currentColor' }} />
          <YAxis tickFormatter={compactCurrency} tickLine={false} axisLine={false} width={80} className="text-xs" tick={{ fill: 'currentColor' }} />
          <Tooltip content={<ChartTooltip />} cursor={{ className: 'fill-muted/40' }} />
          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            onClick={(entry: { payload?: AnnualBarPoint }) => entry.payload && onSelectMonth?.(entry.payload.month)}
            className={onSelectMonth ? 'cursor-pointer' : undefined}
          >
            {chartData.map((d) => (
              <Cell key={d.month} fill={color} fillOpacity={d.isForecast ? 0.35 : 1} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Seletor de ano no mesmo estilo da DRE
 */
export function YearSelect({ value, onChange }: { value: number; onChange: (year: number) => void }) {
  const current = new Date().getFullYear();
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label="Ano"
      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
    >
      {Array.from({ length: 5 }, (_, i) => current - i).map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </select>
  );
}

/**
 * Alternância Mensal | Anual no mesmo estilo das abas da DRE
 */
export function PeriodToggle({ value, onChange }: { value: 'month' | 'year'; onChange: (value: 'month' | 'year') => void }) {
  const option = (key: 'month' | 'year', label: string) => (
    <button
      type="button"
      onClick={() => onChange(key)}
      aria-pressed={value === key}
      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
        value === key
          ? 'bg-blue-600 dark:bg-blue-500 text-white shadow-sm'
          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
      {option('month', 'Mensal')}
      {option('year', 'Anual')}
    </div>
  );
}

/** Mês futuro (ainda não aconteceu) no ano selecionado */
export function isFutureMonth(year: number, month: number): boolean {
  const now = new Date();
  return year * 12 + month > now.getFullYear() * 12 + now.getMonth() + 1;
}
